import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, AlertCircle } from "lucide-react";
import {
  adminCreateTool,
  adminDeleteTool,
  adminListTools,
  adminUpdateTool,
} from "@/admin/cmsApi";
import {
  BilingualField,
  FieldError,
  Label,
  SlugChipPicker,
  TextInput,
} from "@/admin/cmsForms";

const EMPTY = {
  slug: "",
  name: "",
  url: "",
  categories: [],
  desc: { ar: "", en: "" },
  audience: { ar: "", en: "" },
  whenToUse: { ar: "", en: "" },
  howToStart: { ar: "", en: "" },
  logoUrl: "",
  domain: "",
  screenshotUrl: "",
};

export default function AdminToolEditor() {
  const { id } = useParams();          // "new" or UUID
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentId, setCurrentId] = useState(isNew ? null : id);

  const load = useCallback(async () => {
    setErrorMsg("");
    try {
      const data = await adminListTools();
      setCategories(data.categories || []);
      if (!isNew) {
        const match = (data.items || []).find((t) => t.id === id);
        if (!match) {
          setErrorMsg("Tool not found.");
        } else {
          setForm({
            slug: match.slug || "",
            name: match.name || "",
            url: match.url || "",
            categories: match.categories || [],
            desc: match.desc || { ar: "", en: "" },
            audience: match.audience || { ar: "", en: "" },
            whenToUse: match.whenToUse || { ar: "", en: "" },
            howToStart: match.howToStart || { ar: "", en: "" },
            logoUrl: match.logoUrl || "",
            domain: match.domain || "",
            screenshotUrl: match.screenshotUrl || "",
          });
          setCurrentId(match.id);
        }
      }
    } catch (e) {
      setErrorMsg(e.message || "Failed to load tool.");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (fieldErrors[k]) setFieldErrors((e) => ({ ...e, [k]: undefined }));
  };
  const setBilingual = (k, side, v) => {
    setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), [side]: v } }));
    setFieldErrors((e) => ({ ...e, [`${k}.${side}`]: undefined }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setFieldErrors({});
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        url: form.url.trim(),
        categories: form.categories,
        desc: form.desc,
        audience: form.audience,
        whenToUse: form.whenToUse,
        howToStart: form.howToStart,
        logoUrl: (form.logoUrl || "").trim(),
        domain: (form.domain || "").trim(),
        screenshotUrl: (form.screenshotUrl || "").trim(),
      };
      if (isNew || !currentId) {
        const created = await adminCreateTool(payload);
        navigate(`/admin/tools/${created.id}`, { replace: true });
      } else {
        await adminUpdateTool(currentId, payload);
        setErrorMsg(""); // success — state mirrors server
      }
    } catch (err) {
      if (err.status === 422 && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
      } else if (err.status === 409) {
        setFieldErrors({ slug: "Slug already in use. Pick another." });
      } else {
        setErrorMsg(err.message || "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      await adminDeleteTool(currentId);
      navigate("/admin/tools", { replace: true });
    } catch (err) {
      setErrorMsg(err.message || "Delete failed.");
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.label?.en || c.id })),
    [categories]
  );

  if (loading) {
    return (
      <div data-testid="admin-tool-editor-loading" className="space-y-3">
        <div className="h-6 w-32 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="h-10 w-1/2 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="mt-6 h-72 bg-white border border-[color:var(--sas-line)] rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="admin-tool-editor">
      <Link
        to="/admin/tools"
        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] mb-6"
        data-testid="admin-tool-back"
      >
        <ArrowLeft size={14} /> All tools
      </Link>

      <div className="sas-eyebrow mb-2">Content · Tool</div>
      <h1 className="sas-h1">
        {isNew ? "New tool" : (form.name || "Edit tool")}
      </h1>

      {errorMsg && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border border-[color:#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]"
          role="alert"
          data-testid="admin-tool-editor-error"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>{errorMsg}</span>
        </div>
      )}

      <form onSubmit={onSave} className="mt-6 flex flex-col gap-6" noValidate>
        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Core
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tool-name" required>Display name</Label>
              <TextInput id="tool-name" value={form.name} onChange={(v) => setField("name", v)} placeholder="e.g. ChatGPT (free plan)" testid="admin-tool-name" error={fieldErrors.name} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tool-slug" required hint="URL-safe, unique">Slug</Label>
              <TextInput id="tool-slug" value={form.slug} onChange={(v) => setField("slug", v.toLowerCase())} placeholder="e.g. chatgpt-free" dir="ltr" testid="admin-tool-slug" error={fieldErrors.slug} />
            </div>
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="tool-url" required>Tool URL</Label>
              <TextInput id="tool-url" value={form.url} onChange={(v) => setField("url", v)} placeholder="https://…" dir="ltr" testid="admin-tool-url" error={fieldErrors.url} />
            </div>
            {/* Logo overrides — both optional. The frontend resolves
                logos automatically from each tool's URL via Clearbit;
                fill these only when the auto result is wrong. */}
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="tool-domain"
                hint="e.g. openai.com — used for the Clearbit logo"
              >
                Logo domain (optional)
              </Label>
              <TextInput
                id="tool-domain"
                value={form.domain || ""}
                onChange={(v) => setField("domain", v)}
                placeholder="openai.com"
                dir="ltr"
                testid="admin-tool-domain"
                error={fieldErrors.domain}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="tool-logo-url"
                hint="Direct URL to a logo image (SVG / PNG)"
              >
                Logo URL (optional)
              </Label>
              <TextInput
                id="tool-logo-url"
                value={form.logoUrl || ""}
                onChange={(v) => setField("logoUrl", v)}
                placeholder="https://example.com/logo.svg"
                dir="ltr"
                testid="admin-tool-logo-url"
                error={fieldErrors.logoUrl}
              />
            </div>
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <Label
                htmlFor="tool-screenshot-url"
                hint="Hero image for the public /tools/<slug> detail page"
              >
                Screenshot URL (optional)
              </Label>
              <TextInput
                id="tool-screenshot-url"
                value={form.screenshotUrl || ""}
                onChange={(v) => setField("screenshotUrl", v)}
                placeholder="https://example.com/screenshot.png"
                dir="ltr"
                testid="admin-tool-screenshot-url"
                error={fieldErrors.screenshotUrl}
              />
            </div>
            <div className="md:col-span-2 flex flex-col gap-2">
              <Label required>Categories</Label>
              <SlugChipPicker
                allOptions={categoryOptions}
                value={form.categories}
                onChange={(v) => setField("categories", v)}
                testid="admin-tool-categories"
              />
              <FieldError error={fieldErrors.categories} id="admin-tool-categories-error" />
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Description
          </h2>
          <BilingualField
            name="desc"
            label="Short description"
            required
            valueAr={form.desc.ar}
            valueEn={form.desc.en}
            onChangeAr={(v) => setBilingual("desc", "ar", v)}
            onChangeEn={(v) => setBilingual("desc", "en", v)}
            type="textarea"
            rows={3}
            errors={fieldErrors}
          />
          <BilingualField
            name="audience"
            label="Who it’s for"
            valueAr={form.audience.ar}
            valueEn={form.audience.en}
            onChangeAr={(v) => setBilingual("audience", "ar", v)}
            onChangeEn={(v) => setBilingual("audience", "en", v)}
            type="textarea"
            rows={2}
            errors={fieldErrors}
          />
          <BilingualField
            name="whenToUse"
            label="When to use it"
            valueAr={form.whenToUse.ar}
            valueEn={form.whenToUse.en}
            onChangeAr={(v) => setBilingual("whenToUse", "ar", v)}
            onChangeEn={(v) => setBilingual("whenToUse", "en", v)}
            type="textarea"
            rows={2}
            errors={fieldErrors}
          />
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <div>
            <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
              How to start
            </h2>
            <p className="text-[12px] text-[color:var(--sas-ink-2)] mt-1">
              Bilingual paragraph (or short numbered list) shown on the
              public <code>/tools/&lt;slug&gt;</code> detail page. Leave
              empty to hide the section.
            </p>
          </div>
          <BilingualField
            name="howToStart"
            label="Steps / guidance"
            valueAr={(form.howToStart && form.howToStart.ar) || ""}
            valueEn={(form.howToStart && form.howToStart.en) || ""}
            onChangeAr={(v) => setBilingual("howToStart", "ar", v)}
            onChangeEn={(v) => setBilingual("howToStart", "en", v)}
            type="textarea"
            rows={5}
            errors={fieldErrors}
          />
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="sas-btn sas-btn-primary"
            data-testid="admin-tool-save"
          >
            <Save size={15} aria-hidden /> {saving ? "Saving…" : (isNew ? "Create tool" : "Save changes")}
          </button>
          {!isNew && (
            confirmDelete ? (
              <div className="flex items-center gap-2 text-[12.5px] text-[#991b1b]">
                <span>Delete this tool?</span>
                <button type="button" onClick={onDelete} disabled={saving} className="sas-btn sas-btn-primary" style={{ background: "#b91c1c" }} data-testid="admin-tool-delete-confirm">
                  <Trash2 size={15} aria-hidden /> Yes, delete
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="sas-btn sas-btn-ghost" data-testid="admin-tool-delete-cancel">
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="sas-btn sas-btn-ghost" data-testid="admin-tool-delete">
                <Trash2 size={15} aria-hidden /> Delete…
              </button>
            )
          )}
        </div>
      </form>
    </div>
  );
}
